/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, ListItem, Skeleton } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import { SubdirectoryArrowRight } from '@opengeoweb/theme';
import LifeCycleActions from './LifeCycleActions';
import LifeCycleColumn from './LifeCycleColumn';
import LifeCycleEdit from './LifeCycleEdit';
import { getNewInternalStatusTagContent } from './utils';
import { useEvent } from '../../utils/spaceweather-api/hooks';
import { getStatusTagContent } from '../Notifications/utils';

export const getActionModeForDraft = (
  actionMode: string,
  label: string,
  changestateto: string,
): string => {
  if (actionMode !== 'none') {
    return actionMode;
  }
  if (changestateto === 'ended') {
    if (label === 'WARNING') {
      return 'Cancel';
    }
    if (label === 'ALERT') {
      return 'Summarise';
    }
  }

  return actionMode;
};

interface LifeCycleDisplayProps {
  eventId: string;
  toggleDialogOpen: () => void;
  onFormChange?: (hasChanged: boolean) => void;
  setErrorStoreMessage?: (value: string) => void;
  setErrorRetrievePopulate?: (value: string) => void;
}

const LifeCycleDisplay: React.FC<LifeCycleDisplayProps> = ({
  eventId,
  toggleDialogOpen,
  onFormChange,
  setErrorStoreMessage = (): void => {},
  setErrorRetrievePopulate = (): void => {},
}: LifeCycleDisplayProps) => {
  const { data: result, isFetching, error } = useEvent(eventId);
  const [actionMode, setActionMode] = React.useState('none');

  const onAction = (actionType: string): void => {
    setActionMode(actionType);
  };

  const draft =
    result?.lifecycles?.internalprovider?.draft === true
      ? result.lifecycles.internalprovider.notifications![
          result.lifecycles.internalprovider.notifications!.length - 1
        ]
      : false;

  const isInternalProviderLifeCycle =
    result?.lifecycles?.internalprovider?.eventid;

  return (
    <>
      {!!result && (
        <Grid container data-testid="display-lifecycle">
          {result.lifecycles!.externalprovider?.firstissuetime ? (
            <Grid data-testid="display-lifecycle-externalcolumn" size={6}>
              <LifeCycleColumn
                categoryDetail={result.categorydetail}
                lifeCycle={result.lifecycles!.externalprovider}
                type="external"
              />
            </Grid>
          ) : null}
          <Grid
            data-testid="display-lifecycle-internalcolumn"
            size={result.lifecycles!.externalprovider ? 6 : 12}
          >
            {isInternalProviderLifeCycle ? (
              <LifeCycleColumn
                categoryDetail={result.categorydetail}
                lifeCycle={result.lifecycles!.internalprovider!}
                type="internal"
              />
            ) : null}
            <ListItem>
              {
                // internalprovider notification exists already - no action chosen - display buttons
                isInternalProviderLifeCycle &&
                actionMode === 'none' &&
                !draft ? (
                  <LifeCycleActions
                    actions={result.lifecycles!.internalprovider!.canbe!}
                    onAction={onAction}
                  />
                ) : null
              }
              {
                // No internalprovider notification issued yet - incoming externalprovider notification exists - default in data from last external provider notification
                result !== undefined &&
                  !draft &&
                  !isInternalProviderLifeCycle &&
                  actionMode === 'none' && (
                    <LifeCycleEdit
                      baseNotificationData={result}
                      baseNotificationType="externalprovider"
                      eventTypeDisabled
                      onFormChange={onFormChange}
                      setErrorRetrievePopulate={setErrorRetrievePopulate}
                      setErrorStoreMessage={setErrorStoreMessage}
                      statusTagContent={getStatusTagContent(
                        result.lifecycles!.externalprovider!.label,
                        result.categorydetail,
                      )}
                      toggleDialogOpen={toggleDialogOpen}
                    />
                  )
              }

              {
                // internalprovider notification exists already (no draft) - we are performing an action after pressing one of the buttons -
                // base category, type and message off of existing (inernal) event. Any other fields are based off of last external notification if present
                isInternalProviderLifeCycle &&
                !draft &&
                actionMode !== 'none' ? (
                  <Grid
                    container
                    data-testid="lifecycle-display-internaleditaction"
                    spacing={1}
                  >
                    <Grid size={12}>
                      <SubdirectoryArrowRight />
                    </Grid>
                    <Grid size={12}>
                      <LifeCycleEdit
                        actionMode={actionMode}
                        baseNotificationData={result}
                        baseNotificationType="internalprovider"
                        eventTypeDisabled
                        onFormChange={onFormChange}
                        setErrorRetrievePopulate={setErrorRetrievePopulate}
                        setErrorStoreMessage={setErrorStoreMessage}
                        statusTagContent={getNewInternalStatusTagContent(
                          actionMode,
                          result.lifecycles!.internalprovider!.label,
                        )}
                        toggleDialogOpen={toggleDialogOpen}
                      />
                    </Grid>
                  </Grid>
                ) : null
              }

              {
                // internalprovider draft notification exists - base category, type and start/end time off event filled in from draft

                isInternalProviderLifeCycle && draft ? (
                  <Grid
                    container
                    data-testid="lifecycle-display-internaldraftedit"
                    spacing={1}
                  >
                    {result.lifecycles!.internalprovider!.notifications!
                      .length > 1 && (
                      <Grid size={12}>
                        <SubdirectoryArrowRight />
                      </Grid>
                    )}
                    <Grid size={12}>
                      <LifeCycleEdit
                        actionMode={getActionModeForDraft(
                          actionMode,
                          draft.label,
                          draft.changestateto,
                        )}
                        baseNotificationData={result}
                        baseNotificationType="draft"
                        eventTypeDisabled
                        onFormChange={onFormChange}
                        setErrorRetrievePopulate={setErrorRetrievePopulate}
                        setErrorStoreMessage={setErrorStoreMessage}
                        statusTagContent={getStatusTagContent(
                          draft.label,
                          result.categorydetail,
                          draft.changestateto,
                        )}
                        toggleDialogOpen={toggleDialogOpen}
                      />
                    </Grid>
                  </Grid>
                ) : null
              }
            </ListItem>
          </Grid>
        </Grid>
      )}
      {isFetching ? (
        <Skeleton
          data-testid="display-lifecycle-loading"
          height={500}
          variant="rectangular"
          width="100%"
        />
      ) : null}
      {error ? (
        <AlertBanner
          dataTestId="display-lifecycle-error"
          severity="error"
          title={error.message ? error.message : ''}
        />
      ) : null}
    </>
  );
};

export default LifeCycleDisplay;
